import { useQuery } from '@apollo/react-hooks';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Helmet } from 'react-helmet';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { XID } from '@atlassian/atl-cross-domain-tracking';

import { BannerSpacer } from '@confluence/banners';
import {
	Attribution,
	ErrorComponent,
	ErrorDisplay,
	isNotFoundError,
	isUnauthorizedError,
	withErrorBoundary,
} from '@confluence/error-boundary';
import {
	ExperienceFailure,
	ExperienceSuccess,
	VIEW_PUBLIC_LINK_SHARED_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker';
import { analyticsViewPageScreen } from '@confluence/external-share-utils';
import { markErrorAsHandled } from '@confluence/graphql';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { useSessionData, useBooleanFeatureFlag } from '@confluence/session-data';

import type {
	SharedContentQuery as SharedContentQueryType,
	SharedContentQueryVariables,
} from './__types__/SharedContentQuery';
import { ExternalPageNotFoundV2 } from './ExternalPageNotFoundV2';
import { SharedContentQuery } from './SharedContentQuery.graphql';
import type { ContentRenderer } from './ViewPageExternal';
import { ExternalPageBanner } from './banners/ExternalPageBanner';
import { useWithUserTypes } from './hooks/useWithUserTypes';
import { ExternalContent } from './externalContent/ExternalContent';
export interface ExternalSharePageProps {
	/** URL of the site for use in CTA dialogs. Example: `hello.atlassian.net` */
	siteUrl: string;
	/** The content ID of the page to be loaded. Example: `12345678` */
	contentId?: string;
	/** The external share token of the page to be loaded.  Example: 'aaBbC' */
	shareToken: string;
	/** Optional query hash for jumping to a heading. Example: `#Some-Heading` */
	queryHash?: string;
	/**
	 * Content renderer React component. The `@confluence/external-share` package cannot import the
	 * existing fabric renderer (because it's in a classic package) so it is passed in as a prop.
	 */
	ContentRenderer: ContentRenderer;
}

const i18n = defineMessages({
	errorTitle: {
		id: 'external-share.page.error.title',
		defaultMessage: 'Could not load page content',
		description: 'title of message displayed when there is an error showing the content',
	},
});

const getDaysSince = (pastDateStr: string | null | undefined) => {
	if (!pastDateStr) return -1;

	const currentDate = new Date();
	const pastDate = new Date(pastDateStr);

	const daysSinceCreation = Math.round(
		(currentDate.getTime() - pastDate.getTime()) / (24 * 60 * 60 * 1000),
	);
	return daysSinceCreation;
};

const renderPublicLinkMetaTags = () => (
	<Helmet>
		<meta name="robots" content="noindex" />
	</Helmet>
);

export const ExternalSharePage = withErrorBoundary({
	attribution: Attribution.EXTERNAL_COLLAB,
})(({ contentId, shareToken, queryHash, ContentRenderer }: ExternalSharePageProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userType } = useWithUserTypes();
	const isMobileView = shouldShowMobileWeb();
	const isSpaceAliasFFEnabled = useBooleanFeatureFlag('confluence.frontend.space.alias');

	const sharedContentQuery = useQuery<SharedContentQueryType, SharedContentQueryVariables>(
		SharedContentQuery,
		{
			variables: {
				shareToken,
				contentId,
				includeAlias: isSpaceAliasFFEnabled,
			},
			context: {
				single: true,
				allowOnExternalPage: true,
			},
			fetchPolicy: 'cache-and-network',
		},
	);

	// Here unauthorized errors are mostly due to public link turned off.  Since
	// such an error is expected, we will fire a success event instead.
	// https://atlassian.slack.com/archives/C03JRVC8SLQ/p1663889729868609?thread_ts=1663193930.178289&cid=C03JRVC8SLQ
	if (isUnauthorizedError(sharedContentQuery.error)) {
		markErrorAsHandled(sharedContentQuery.error);
	}

	const { cloudId } = useSessionData();

	const sharedContent = sharedContentQuery.data?.singleContent;
	const contentType = sharedContent?.type;
	const space = sharedContent?.space;

	const spaceKey = (isSpaceAliasFFEnabled && space?.alias) || space?.key;
	// NOSONAR invalid finding
	const retrievedContentId = sharedContent?.id!;
	const validContentId = contentId || retrievedContentId;

	useEffect(
		() => {
			const spaceId = space?.id;
			const xid = new XID();
			const sendScreenEvent = async () => {
				const xidResult = await xid.getXidPromise();
				analyticsViewPageScreen(createAnalyticsEvent, spaceId, validContentId, {
					pageType: sharedContent?.type,
					daysSinceCreated: getDaysSince(sharedContent?.history?.createdDate),
					daysSinceModified: getDaysSince(sharedContent?.history?.lastUpdated?.when),
					isPublicView: true,
					xid: xidResult,
				});
			};
			if (!sharedContentQuery.loading) void sendScreenEvent();
		},
		// We'll only want to fire this screen event once, after the spaceId has resolved.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sharedContentQuery.loading],
	);

	const externalPageNotFound = isNotFoundError(sharedContentQuery.error);

	if (
		externalPageNotFound ||
		// The backend is still returning null data for a valid public link, if the site/space/page
		// toggle is off.
		(!sharedContentQuery.loading && !sharedContentQuery.error && !sharedContent)
	) {
		if (externalPageNotFound) markErrorAsHandled(sharedContentQuery.error);
		return (
			<>
				{renderPublicLinkMetaTags()}
				<ExternalPageNotFoundV2 />
			</>
		);
	}

	const Banner = (
		<>
			{renderPublicLinkMetaTags()}
			<ExternalPageBanner
				userType={userType}
				cloudId={cloudId}
				spaceKey={spaceKey}
				contentId={validContentId}
				isMobileView={isMobileView}
				contentType={contentType}
				shareToken={shareToken}
			/>
			<BannerSpacer />
		</>
	);

	return (
		<>
			{sharedContentQuery.error ? (
				isUnauthorizedError(sharedContentQuery.error) ? (
					<>
						<ExperienceSuccess name={VIEW_PUBLIC_LINK_SHARED_PAGE_EXPERIENCE} />
						<ExternalPageNotFoundV2 />
					</>
				) : (
					<>
						{Banner}
						<ErrorDisplay error={sharedContentQuery.error}>
							<ExperienceFailure
								name={VIEW_PUBLIC_LINK_SHARED_PAGE_EXPERIENCE}
								error={sharedContentQuery.error}
							/>
							<ErrorComponent
								error={sharedContentQuery.error}
								attribution={Attribution.EXTERNAL_COLLAB}
								customTitle={intl.formatMessage(i18n.errorTitle)}
							/>
						</ErrorDisplay>
					</>
				)
			) : (
				<>
					{Banner}
					<ExternalContent
						contentId={validContentId}
						shareToken={shareToken}
						sharedContentQuery={sharedContentQuery}
						queryHash={queryHash}
						ContentRenderer={ContentRenderer}
						isSpaceAliasFFEnabled={isSpaceAliasFFEnabled}
					/>
				</>
			)}
		</>
	);
});
